import {
    createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import type { ReactNode } from 'react';

import { apiFetch } from './api';
import { useAuth } from './auth';
import type { Unit } from './types';

// Active-unit context. The portal is single-unit-at-a-time per SPEC § 6, with
// no "all units" aggregate. The chosen `cntID` survives refreshes via
// localStorage; if it points to a now-inactive unit the first active one is
// re-selected.

const STORAGE_KEY = 'eit.activeCntId';

interface UnitsApi {
    units: Unit[];
    activeCntId: number | null;
    active: Unit | null;
    loading: boolean;
    error:   string | null;
    setActive: (cntId: number) => void;
    reload:    () => Promise<void>;
}

const Ctx = createContext<UnitsApi | null>(null);

export function useUnits(): UnitsApi {
    const v = useContext(Ctx);
    if (!v) throw new Error('useUnits must be used within <UnitsProvider>');
    return v;
}

interface ListResponse { success: true; units: Unit[] }

export function UnitsProvider({ children }: { children: ReactNode }) {
    const { user, status } = useAuth();
    const [units, setUnits]   = useState<Unit[]>([]);
    const [activeCntId, setActiveCntId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const reload = useCallback(async () => {
        if (status !== 'authed') return;
        setLoading(true); setError(null);
        try {
            const r = await apiFetch<ListResponse>('/api/units');
            setUnits(r.units);

            // Recover or initialize the active unit.
            const stored = Number(localStorage.getItem(STORAGE_KEY) || '0');
            const stillExists = r.units.find((u) => u.cntID === stored);
            const chosen = stillExists ?? r.units[0] ?? null;
            if (chosen) {
                setActiveCntId(chosen.cntID);
                localStorage.setItem(STORAGE_KEY, String(chosen.cntID));
            } else {
                setActiveCntId(null);
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, [status]);

    // Load the unit list when the user signs in; clear it on sign-out.
    useEffect(() => {
        if (status === 'authed' && user) void reload();
        else if (status === 'guest') {
            setUnits([]); setActiveCntId(null); setError(null);
        }
    }, [status, user, reload]);

    const setActive = useCallback((cntId: number) => {
        if (units.find((u) => u.cntID === cntId)) {
            setActiveCntId(cntId);
            localStorage.setItem(STORAGE_KEY, String(cntId));
        }
    }, [units]);

    const active = useMemo(
        () => units.find((u) => u.cntID === activeCntId) ?? null,
        [units, activeCntId],
    );

    const api = useMemo<UnitsApi>(() => ({
        units, activeCntId, active, loading, error, setActive, reload,
    }), [units, activeCntId, active, loading, error, setActive, reload]);

    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}
